/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useEffect, type SubmitEvent } from 'react';

import { useQuery } from '@apollo/client/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Checkbox, Divider, FormControlLabel, FormGroup, Stack, Tooltip, useMediaQuery } from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import dayjs, { type Dayjs } from 'dayjs';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router';

import type { FilterInputType, OfficeType, OptionsType } from '../../../Data/types';
import type { OfficesQuery } from '../../../gql/graphql';
import {
  GET_AUDIENCE_NAMES,
  GET_DEPARTMENTS,
  GET_EVENT_TYPES,
  GET_OFFICES,
  GET_PRACTICES,
  GET_OPTION_VALUES,
  QRY_GET_USER,
} from '../../../graphql/queries';
import DropdownSelect from '../../shared/DropdownSelect/DropdownSelect';
import { Field } from '../../shared/Field/Field';
import StartDateFilter from '../DateFilters/DateFilter';
import TextInput from '../TextInput';

type Props = {
  isInitialSize: boolean;
};

const parseDateRange = (startDate: string | null, endDate: string | null): DateRange<Dayjs> => {
  if (!startDate && !endDate) {
    return [null, null];
  }
  return [startDate ? dayjs(startDate.replace(/-/g, '/')) : null, endDate ? dayjs(endDate.replace(/-/g, '/')) : null];
};
const serializeOptionsFilter = (options: OptionsType[]): string => {
  return options.map((o) => `${o.id},${o.name}`).join('|');
};
const serializeOfficeFilter = (offices: OfficeType[]): string => {
  return offices.map((o) => o.id).join('|');
};
const deserializeOptionsFilter = (optionStr: string | null): OptionsType[] => {
  if (!optionStr) return [];
  return optionStr.split('|').map((item) => {
    const [idStr, name] = item.split(',');
    return { id: Number.parseInt(idStr, 10), name };
  });
};

const deserializeHostingTypeFilter = (hostingType: string | null): string[] => {
  if (!hostingType) return [];
  return hostingType.split(',');
};

const deserializeOfficeFilter = (officeStr: string | null, officesData?: OfficesQuery): OfficeType[] => {
  if (!officeStr) return [];
  return officeStr.split('|').map((id) => {
    const office = officesData?.gec_offices?.find((o) => o.id === id);
    return {
      id,
      office_code: office?.office_code || '',
      name: office?.name || '',
      address: office?.address || '',
    };
  });
};

const OPTIONS_KEYS = new Set(['practice', 'eventType', 'audience', 'department', 'industries', 'firmInitiatives']);

const appendDateRange = (params: URLSearchParams, value: unknown[]): void => {
  if (value[0]) params.append('startDate', (value[0] as Dayjs).format('YYYY-MM-DD'));
  if (value[1]) params.append('endDate', (value[1] as Dayjs).format('YYYY-MM-DD'));
};

const appendFilterParam = (params: URLSearchParams, key: string, value: unknown): void => {
  if (!value) return;
  if (key === 'eventDateRange' && Array.isArray(value)) {
    appendDateRange(params, value);
    return;
  }
  if (Array.isArray(value) && value.length === 0) return;
  if (OPTIONS_KEYS.has(key) && Array.isArray(value)) {
    params.set(key, serializeOptionsFilter(value as OptionsType[]));
    return;
  }
  if ((key === 'internalLocation' || key === 'officeVisibility') && Array.isArray(value)) {
    params.set(key, serializeOfficeFilter(value as OfficeType[]));
    return;
  }
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === 'string')) {
      params.append(key, value.join(','));
    } else if (value.every((v) => typeof v === 'object' && v !== null && 'id' in v)) {
      value.forEach((v) => params.append(key, (v as { id: number }).id.toString()));
    }
    return;
  }
  params.append(key, typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value));
};

function FiltersQuery({ isInitialSize }: Readonly<Props>) {
  const location = useLocation();
  const [filterParams, setFilterParams] = useSearchParams();
  const { data } = useQuery(QRY_GET_USER);
  const { data: offices } = useQuery(GET_OFFICES);

  const getDefaultValues = (params: URLSearchParams): FilterInputType => {
    return {
      eventName: params.get('eventName') ?? '',
      hostingType: deserializeHostingTypeFilter(params.get('hostingType')),
      internalLocation: deserializeOfficeFilter(params.get('internalLocation'), offices),
      externalLocation: params.get('externalLocation') ?? '',
      eventDateRange: parseDateRange(params.get('startDate'), params.get('endDate')),
      practice: deserializeOptionsFilter(params.get('practice')),
      audience: deserializeOptionsFilter(params.get('audience')),
      department: deserializeOptionsFilter(params.get('department')),
      eventType: deserializeOptionsFilter(params.get('eventType')),
      industries: deserializeOptionsFilter(params.get('industries')),
      firmInitiatives: deserializeOptionsFilter(params.get('firmInitiatives')),
      officeVisibility: deserializeOfficeFilter(params.get('officeVisibility'), offices),
    };
  };

  const { handleSubmit, control, reset } = useForm<FilterInputType>({
    values: getDefaultValues(filterParams),
  });

  const getCurrentUserOffice = useMemo(() => {
    return (data: any, offices: OfficesQuery | undefined) => {
      const office = offices?.gec_offices.find((o) =>
        o.name?.toLowerCase().includes(data?.me?.officeLocation?.toLowerCase()),
      );
      return office ? [office as OfficeType] : [];
    };
  }, []);

  useEffect(() => {
    if (data && offices && !location.pathname.includes('/event/')) {
      const currentUserOffice = getCurrentUserOffice(data, offices);
      if (filterParams.size === 0 && currentUserOffice.length > 0 && isInitialSize) {
        // Find firmwide and virtual offices
        const extraOffices = offices.gec_offices.filter((o) => {
          const name = o.name?.toLowerCase();
          return name === 'firmwide' || name === 'virtual';
        });
        const allOfficeIds = [...currentUserOffice.map((o) => o.id), ...extraOffices.map((o) => o.id)];
        const newParams = new URLSearchParams();
        newParams.set('officeVisibility', allOfficeIds.join('|'));
        setFilterParams(newParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, offices]);

  const onSubmit: SubmitHandler<FilterInputType> = (data) => {
    const newParams = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
      appendFilterParam(newParams, key, value);
    }
    setFilterParams(newParams);
  };

  const onReset = () => {
    reset(getDefaultValues(filterParams));
    setFilterParams({});
  };

  const onFormSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    void handleSubmit(onSubmit)(event);
  };

  const IsSmallScreenHeight = useMediaQuery('(max-height:1280px)');

  return (
    <form onSubmit={onFormSubmit} onReset={onReset}>
      <Stack direction="column" spacing={IsSmallScreenHeight ? 0.1 : 0.5}>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Tooltip title="Click to reset all filter fields" arrow placement="top">
            <Button size="small" startIcon={<CloseIcon fontSize="small" />} type="reset" color="inherit" variant="text">
              Reset
            </Button>
          </Tooltip>
          <Tooltip title="Click to apply filters" arrow placement="top">
            <Button
              type="submit"
              variant="text"
              color="inherit"
              size="small"
              startIcon={<CheckIcon fontSize="small" />}
            >
              Apply
            </Button>
          </Tooltip>
        </Stack>
        <Divider>Filters</Divider>
        <Controller
          name="eventName"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextInput
              label="Event Name"
              inputProps={{ placeholder: 'Search by event name' }}
              value={value ?? ''}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name="hostingType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Field label="Hosting Type" fullWidth tooltip="Select hosting type">
              <FormGroup row>
                <FormControlLabel
                  labelPlacement="end"
                  control={
                    <Checkbox
                      size="small"
                      checked={value?.includes('internal') ?? false}
                      onChange={(e) => {
                        const newValue = e.target.checked
                          ? [...(value ?? []), 'internal']
                          : (value ?? []).filter((v) => v !== 'internal');
                        onChange(newValue);
                      }}
                      {...{ name: 'internal' }}
                    />
                  }
                  label="Internal"
                />
                <FormControlLabel
                  labelPlacement="end"
                  control={
                    <Checkbox
                      size="small"
                      checked={value?.includes('external') ?? false}
                      onChange={(e) => {
                        const newValue = e.target.checked
                          ? [...(value ?? []), 'external']
                          : (value ?? []).filter((v) => v !== 'external');
                        onChange(newValue);
                      }}
                      {...{ name: 'external' }}
                    />
                  }
                  label="External"
                />
              </FormGroup>
            </Field>
          )}
        />
        <Controller
          name="internalLocation"
          control={control}
          render={({ field }) => (
            <Field label="Office Location" tooltip="Select office location" fullWidth>
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_OFFICES}
                placeholder="Select Location"
                optionValueKey="gec_offices"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="externalLocation"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextInput
              label="Location (External only)"
              inputProps={{ placeholder: 'External location' }}
              value={value ?? ''}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name="officeVisibility"
          control={control}
          render={({ field }) => (
            <Field label="Calendar Visibility" tooltip="Select what calendars can view this event" fullWidth>
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_OFFICES}
                placeholder="Select Location"
                optionValueKey="gec_offices"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="eventDateRange"
          control={control}
          render={({ field: { onChange, value } }) => <StartDateFilter dateFilter={value} setDateFilter={onChange} />}
        />
        <Controller
          name="practice"
          control={control}
          render={({ field }) => (
            <Field label="Practice Group" tooltip="Choose one or more practice group" fullWidth>
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_PRACTICES}
                placeholder="Select Practice"
                optionValueKey="gec_practice_groups_rollup"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="department"
          control={control}
          render={({ field }) => (
            <Field label="Department" tooltip="Choose one or more department" fullWidth>
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_DEPARTMENTS}
                placeholder="Select Department"
                optionValueKey="gec_departments"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="audience"
          control={control}
          render={({ field }) => (
            <Field label="Audience" tooltip="Choose one or more Audience" fullWidth>
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_AUDIENCE_NAMES}
                placeholder="Select Audience"
                optionValueKey="gec_audience"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="eventType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Field label="Event Type" tooltip="Choose one or more Event Types" fullWidth>
              <DropdownSelect
                value={value}
                onChange={onChange}
                query={GET_EVENT_TYPES}
                placeholder="Select Event Type"
                optionValueKey="gec_event_type"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="industries"
          control={control}
          render={({ field }) => (
            <Field
              label="Industry, Strategic Initiative, or Task Force"
              tooltip="Choose one or more Industry, Strategic Initiative, or Task Force"
              fullWidth
            >
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_OPTION_VALUES}
                placeholder="Select Industry, Strategic Initiative, or Task Force"
                optionValueKey="gec_industries"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
        <Controller
          name="firmInitiatives"
          control={control}
          render={({ field }) => (
            <Field label="Firm Initiative" tooltip="Choose one or more Firm Initiative" fullWidth>
              <DropdownSelect
                value={field.value}
                onChange={field.onChange}
                query={GET_OPTION_VALUES}
                placeholder="Select Firm Initiative"
                optionValueKey="gec_firm_initiatives"
                multiple
                limitTags={2}
              />
            </Field>
          )}
        />
      </Stack>
    </form>
  );
}
export default FiltersQuery;
